// The campaign composer's cost meter: renders the message the way the real
// send path will (sms-jobs/actions/campaigns.ts renders message_body through
// renderTemplate before calculateSmsSegments), then reports length, encoding,
// segments, cost, and concrete tips that make the send cheaper. Pure module,
// no IO — unit-tested from src/test/smsCostMeter.test.ts.
//
// It must never grow a second implementation of rendering or segment math:
// both come from the shared modules the send worker itself uses.

import { assignHoldouts, renderTemplate, type HoldoutAudienceMember, type TemplateVars } from "@/utils/smsShared";

export interface SendCounts {
  audience: number;
  heldOut: number;
  texted: number;
}

/** Splits an audience into held-out and texted counts using the send path's
 *  own assignHoldouts (a per-member hash test, not a rounded percentage), so
 *  for the same campaign id the numbers match what materialisation holds out. */
export function planSendCounts(
  members: HoldoutAudienceMember[],
  campaignId: string,
  holdoutPct: number,
  now: Date = new Date(),
): SendCounts {
  const heldOut = assignHoldouts(members, campaignId, holdoutPct, now).filter((a) => a.is_holdout).length;
  return { audience: members.length, heldOut, texted: members.length - heldOut };
}
import { calculateSmsSegments, isMms, isGsm7Char } from "@/utils/smsSegments";

/** Offer codes are 10 base62 chars (codes.ts generateOfferCode), so a sample
 *  link of the same shape gives the meter the exact as-sent length. */
const SAMPLE_OFFER_CODE = "XXXXXXXXXX";

/** GSM-7 lookalikes for the common characters that force a message into
 *  Unicode (UCS-2) mode. Anything not listed (emoji, most non-Latin script)
 *  has no safe swap and the tip says to remove it instead. */
const UNICODE_SWAPS: Record<string, string> = {
  "—": "-", // em dash
  "–": "-", // en dash
  "‘": "'", // left single quote
  "’": "'", // right single quote
  "“": '"', // left double quote
  "”": '"', // right double quote
  "…": "...", // ellipsis
  " ": " ", // non-breaking space
  "•": "-", // bullet
};

export interface CostMeterSettings {
  public_base_url: string;
  store_name: string;
  store_phone: string | null;
  store_address: string;
  sms_cost_per_segment: number;
}

export interface CostMeterInput {
  messageBody: string;
  settings: CostMeterSettings;
  audienceCount: number;
  hasImage: boolean;
  /** Extra template vars (e.g. promo_title) merged over the settings-derived ones. */
  vars?: Partial<TemplateVars>;
}

export interface OffendingChar {
  char: string;
  /** Index into the RENDERED body. */
  index: number;
  /** GSM-7 replacement, or null when there is none (remove it instead). */
  suggestion: string | null;
}

export interface CostTip {
  kind: "unicode_swap" | "unicode_remove" | "trim_to_single" | "mms_image" | "headroom";
  message: string;
}

export interface CostMeterResult {
  /** The body as it will actually send, placeholders expanded. */
  renderedBody: string;
  charCount: number;
  encoding: "GSM-7" | "UCS-2";
  segments: number;
  charsPerSegment: number;
  charsLeft: number;
  isMms: boolean;
  costPerPerson: number;
  totalCost: number;
  offendingChars: OffendingChar[];
  tips: CostTip[];
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** The template vars the real send path fills (campaigns.ts:145), with a
 *  worst-case-length sample offer link. Exported so previews render the same
 *  body the meter counts. */
export function sampleSendVars(settings: CostMeterSettings, vars?: Partial<TemplateVars>): Partial<TemplateVars> {
  const base = settings.public_base_url.replace(/\/+$/, "");
  return {
    location_name: settings.store_name,
    offer_link: `${base}/o/${SAMPLE_OFFER_CODE}`,
    store_phone: settings.store_phone ?? "",
    store_address: settings.store_address,
    ...vars,
  };
}

export function calculateCostMeter(input: CostMeterInput): CostMeterResult {
  const { messageBody, settings, audienceCount, hasImage } = input;

  const renderedBody = renderTemplate(messageBody, sampleSendVars(settings, input.vars));
  const seg = calculateSmsSegments(renderedBody);

  // charCount as the meter reports it: septets for GSM-7 (extension chars
  // count double, matching calculateSmsSegments), code units for UCS-2.
  const charCount =
    seg.encoding === "GSM-7"
      ? seg.segments === 1
        ? seg.charsPerSegment - seg.charsLeft
        : seg.segments * seg.charsPerSegment - seg.charsLeft
      : renderedBody.length;

  const offendingChars: OffendingChar[] = [];
  if (seg.encoding === "UCS-2") {
    const seen = new Set<string>();
    // Iterate code points, not code units, so an emoji reports as one
    // character instead of two broken surrogate halves.
    let index = 0;
    for (const ch of renderedBody) {
      if (!isGsm7Char(ch) && !seen.has(ch)) {
        seen.add(ch);
        offendingChars.push({ char: ch, index, suggestion: UNICODE_SWAPS[ch] ?? null });
      }
      index += ch.length;
    }
  }

  const mms = isMms(renderedBody, hasImage);
  const costPerPerson = round2(seg.segments * settings.sms_cost_per_segment);
  const totalCost = round2(seg.segments * settings.sms_cost_per_segment * audienceCount);

  const tips: CostTip[] = [];

  if (seg.encoding === "UCS-2") {
    const swappable = offendingChars.filter((c) => c.suggestion !== null);
    const unswappable = offendingChars.filter((c) => c.suggestion === null);
    if (swappable.length > 0 && unswappable.length === 0) {
      // Every culprit has a plain-text twin: quantify the win of swapping.
      let swapped = renderedBody;
      for (const { char, suggestion } of swappable) {
        swapped = swapped.split(char).join(suggestion as string);
      }
      const after = calculateSmsSegments(swapped);
      const savedPerPerson = round2((seg.segments - after.segments) * settings.sms_cost_per_segment);
      const savedTotal = round2(savedPerPerson * audienceCount);
      const list = swappable.map((c) => `"${c.char}" with "${c.suggestion}"`).join(", ");
      tips.push({
        kind: "unicode_swap",
        message:
          `${describeChars(swappable)} switches this to Unicode mode (${seg.charsPerSegment} characters per text instead of 160). ` +
          `Replace ${list} to send as ${after.segments} text${after.segments === 1 ? "" : "s"} per person` +
          (savedTotal > 0 ? `, saving about $${savedTotal.toFixed(2)} on this send.` : "."),
      });
    } else if (unswappable.length > 0) {
      tips.push({
        kind: "unicode_remove",
        message:
          `${describeChars(offendingChars)} switches this to Unicode mode (${seg.charsPerSegment} characters per text instead of 160). ` +
          `Removing ${unswappable.map((c) => `"${c.char}"`).join(", ")}` +
          (offendingChars.some((c) => c.suggestion) ? " and swapping the rest" : "") +
          " brings the plain-text limit back.",
      });
    }
  } else if (seg.segments > 1) {
    const over = charCount - 160;
    if (over > 0 && over <= 25) {
      tips.push({
        kind: "trim_to_single",
        message: `Trim ${over} character${over === 1 ? "" : "s"} to fit in 1 text per person (cuts the cost to $${round2(
          settings.sms_cost_per_segment * audienceCount,
        ).toFixed(2)}).`,
      });
    }
  } else {
    tips.push({
      kind: "headroom",
      message: `${seg.charsLeft} character${seg.charsLeft === 1 ? "" : "s"} of headroom before this splits into 2 texts.`,
    });
  }

  if (hasImage) {
    tips.push({
      kind: "mms_image",
      message: "The image makes this an MMS, which costs more than a plain text. Remove it to send as SMS.",
    });
  }

  return {
    renderedBody,
    charCount,
    encoding: seg.encoding,
    segments: seg.segments,
    charsPerSegment: seg.charsPerSegment,
    charsLeft: seg.charsLeft,
    isMms: mms,
    costPerPerson,
    totalCost,
    offendingChars,
    tips,
  };
}

function describeChars(chars: OffendingChar[]): string {
  if (chars.length === 1) return `The character "${chars[0].char}"`;
  return `${chars.length} characters (${chars.map((c) => `"${c.char}"`).join(", ")})`;
}
